import { cartDisplayName } from './cart-display'

/** 尺寸文案去「长」前缀（与报价/编辑页一致） */
export function formatSizeText(text) {
	if (text === undefined || text === null || text === '' || text === '-') return text
	return String(text).replace(/长/g, '')
}

export function isEmptyDimValue(val) {
	return val === undefined || val === null || val === ''
}

/**
 * 解析单行可计入的「长度」mm：
 * - valuation=1 / size pending → 0
 * - 优先 goods_long
 * - 否则从 size_text 取「长」后数字；仅单项数字则取该值
 * - 高×宽×厚（无长）或无法解析 → 0
 */
export function parseCartLengthMm(row, options = {}) {
	if (!row || Number(row.goods_valuation) === 1) return 0
	if (typeof options.isSizePending === 'function' && options.isSizePending(row)) return 0
	if (!isEmptyDimValue(row.goods_long)) {
		const long = Number(row.goods_long)
		if (Number.isFinite(long)) return long
	}
	const raw = row.size_text != null ? String(row.size_text) : ''
	if (!raw || raw === '-') return 0
	const longMatch = raw.match(/长\s*([0-9]+(?:\.[0-9]+)?)/)
	if (longMatch) {
		const n = Number(longMatch[1])
		return Number.isFinite(n) ? n : 0
	}
	if (/高/.test(raw)) return 0
	const text = String(formatSizeText(raw) || '').trim()
	if (!text || text === '-') return 0
	if (/^[0-9]+(?:\.[0-9]+)?$/.test(text)) {
		const n = Number(text)
		return Number.isFinite(n) ? n : 0
	}
	const first = text.split(/[x×X]/)[0]
	if (first && /^[0-9]+(?:\.[0-9]+)?$/.test(first.trim())) {
		const n = Number(first.trim())
		return Number.isFinite(n) ? n : 0
	}
	return 0
}

function formatStatNumber(n) {
	if (!Number.isFinite(n) || n === 0) return '0'
	return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100)
}

/** 稳定分组键：商品 + SKU + 规格参数 */
export function buildStatsGroupKey(row) {
	if (!row) return ''
	const gid = String(row.goods_id ?? '')
	const sku = String(row.sku_ids ?? '')
	const paramIds = String(row.parameter_ids ?? '').trim()
	const paramText = String(row.parameter_text ?? '').trim()
	return [gid, sku, paramIds || paramText].join('\u0001')
}

/** 展示名：名称列 + 规格（规格已含于名称则不重复） */
export function buildStatsGroupLabel(row) {
	const name = cartDisplayName(row) || '未命名'
	const param = String((row && row.parameter_text) || '').trim()
	if (!param) return name
	if (name.indexOf(param) !== -1) return name
	return name + ' - ' + param
}

/**
 * 清单统计（启发式 A）：
 * - 有有效长度 → 仅进长度组（Σ 长×数量，并累计支数）
 * - 否则进数量组（Σ 数量）
 * - 分组：名称+规格（稳定键 goods_id+sku+parameter）
 */
export function buildCartGoodsStats(rows, options = {}) {
	const list = Array.isArray(rows) ? rows : []
	const isNumPending = typeof options.isNumPending === 'function' ? options.isNumPending : () => false
	const isSizePending = typeof options.isSizePending === 'function' ? options.isSizePending : () => false
	const lengthMap = new Map()
	const qtyMap = new Map()

	list.forEach((row) => {
		if (!row || isNumPending(row)) return
		const qty = Number(row.goods_num)
		if (!Number.isFinite(qty) || qty <= 0) return

		const len = parseCartLengthMm(row, { isSizePending })
		const key = buildStatsGroupKey(row)
		const label = buildStatsGroupLabel(row)

		if (len > 0) {
			const prev = lengthMap.get(key) || { key, label, length: 0, qty: 0 }
			prev.length += len * qty
			prev.qty += qty
			prev.label = label
			lengthMap.set(key, prev)
			return
		}

		const prev = qtyMap.get(key) || { key, label, qty: 0 }
		prev.qty += qty
		prev.label = label
		qtyMap.set(key, prev)
	})

	const lengthGroups = Array.from(lengthMap.values()).map((g) => ({
		key: g.key,
		label: g.label,
		kind: 'length',
		length: g.length,
		lengthText: formatStatNumber(g.length),
		qty: g.qty,
		qtyText: formatStatNumber(g.qty),
		/** 兼容旧字段 */
		value: g.length,
		valueText: formatStatNumber(g.length)
	}))
	const qtyGroups = Array.from(qtyMap.values()).map((g) => ({
		key: g.key,
		label: g.label,
		kind: 'qty',
		length: 0,
		lengthText: '',
		qty: g.qty,
		qtyText: formatStatNumber(g.qty),
		value: g.qty,
		valueText: formatStatNumber(g.qty)
	}))

	/** 明细行统一列表，便于表单列对齐 */
	const detailRows = lengthGroups.concat(qtyGroups)
	const totalLength = lengthGroups.reduce((sum, g) => sum + g.length, 0)
	const totalNum = qtyGroups.reduce((sum, g) => sum + g.qty, 0)
	const productTypeCount = detailRows.length

	return {
		totalLength,
		totalLengthText: formatStatNumber(totalLength),
		totalNum,
		totalNumText: formatStatNumber(totalNum),
		productTypeCount,
		productTypeText: formatStatNumber(productTypeCount),
		lengthGroups,
		qtyGroups,
		detailRows
	}
}
